<template>
    <DsTextInput
        v-model="model"
        v-bind="{ ...$props, ...$attrs }"
        class="ds-textarea"
        :error="errorMessage"
        type=""
    >
        <template
            v-for="(_, slot) in $slots"
            #[slot]="scope"
        >
            <slot
                :name="slot"
                v-bind="scope"
            />
        </template>

        <template #input="{ inputClass, elementId }">
            <div
                class="flex flex-1 flex-col"
                :class="{ 'resize-y overflow-hidden': isResizable, 'pb-3': isResizable && !characterLimitState }"
            >
                <textarea
                    v-model="model"
                    :id="elementId"
                    ref="textareaElement"
                    :autofocus
                    :class="[inputClass, 'resize-none', { 'has-error': !!errorMessage }]"
                    :disabled
                    :placeholder="!disabled ? placeholder : ''"
                    :readonly
                    :rows
                />
                <span
                    v-if="characterLimitState"
                    class="self-end leading-none"
                    :class="[
                        { 'mr-4': isResizable },
                        size === 'sm' ? 'text-content-sm' : 'text-content-md',
                        characterLimitState.excess ? 'text-content-error' : 'text-content-secondary',
                    ]"
                    data-testid="ds-textarea-character-counter"
                >
                    {{ characterLimitState.remaining }}
                </span>
            </div>
        </template>
    </DsTextInput>
</template>

<script setup lang="ts">
import DsTextInput from '@ds/form/input/text/DsTextInput.vue';
import type { DsTextareaProps } from '@ds/form/input/text/text-input.types';
import { getCharacterLimitState } from '@ds/form/input/text/text-input.utils';
import { computed, useTemplateRef } from 'vue';

const model = defineModel<string>({ default: '' });

const props = withDefaults(defineProps<DsTextareaProps>(), {
    rows: 5,
    resizable: true,
    maxlength: 0,
});

const isResizable = computed(() => props.resizable && !props.disabled);

const characterLimitState = computed(() => getCharacterLimitState(model.value, props.maxlength));

const errorMessage = computed(() => {
    if (typeof props.error === 'string' && props.error) {
        return props.error;
    }

    return characterLimitState.value?.error || props.error;
});

const textareaElement = useTemplateRef('textareaElement');

defineExpose({
    textareaElement,
});
</script>

<style scoped lang="scss">
.ds-textarea {
    :deep(.ds-input-inner-wrapper) {
        @apply h-auto;
    }

    :deep(.input-field) {
        @apply h-auto;
    }
}
</style>
